// client/src/features/health/enums.ts — the band enum, the five components and the seventeen
// penalty codes · HS-003 (04 §8.13, 01 §24.2).
//
// `features/notifications/enums.ts` is the construction, and the distinction it draws applies
// here exactly:
//
//   • `HealthScoreBand` is a real enum. The generated client gives it as a string-literal
//     union, a union has no runtime value, and the exhaustive `Record<T, true>` below is what
//     makes `tsc` fail in the PR that regenerates the client if a member is added or dropped.
//     Its labels live under `enums.HealthScoreBand.{VALUE}` — 04 §9.6.3's ONE sanctioned
//     computed key — and `__tests__/enum-parity.test.ts` holds them to the domain enum.
//
//   • The component codes and the penalty codes are NOT enums. `HealthScoreComponent.Component`
//     and `HealthPenalty.Code` are `string` on the wire because they are written into a jsonb
//     document that outlives any C# type (HS-002's own note on `HealthScorePenalties`), and a
//     snapshot computed under an older catalogue must stay readable after a code is retired. So
//     the compiler can hold nothing to them, the label tables below are keyed by string, and
//     both have an explicit fallback for a value this build has never heard of.
//
// THE LABELS ARE CLOSURES OVER `t`, NEVER KEY STRINGS. A stored key would be `t(table[code])`,
// a computed key the §9.6.5 gate fails and a translator never receives. A closure keeps the
// literal at the call site while the table stays a table.
//
// WHAT A PENALTY LABEL IS, given the server already sends a sentence. `messageRo` is the
// SPECIFIC sentence — „Chirie restantă de 2.400,00 lei, veche de 41 de zile" — composed by the
// calculator from this property's own figures. The label is the RULE's name, the thing that is
// the same on every property it fires on. 04 §8.13 asks the drawer to render „`code`, points,
// message and a deep link"; the raw code is rendered beside the label rather than instead of
// it, because `FIN_NEG_OPERATING_3M` is what support and the ledger call this rule and the
// landlord should not have to read it to know what it means.

import type { TFunction } from 'i18next';

import type { HealthScoreBand } from '@/client';
import { fiscalVisible } from '@/config/fiscal';

/** 02 §13's three bands, best first — the order 04 §8.13 prints them in. */
const HEALTH_SCORE_BAND_ORDER: Record<HealthScoreBand, true> = {
  GREEN: true,
  AMBER: true,
  RED: true,
};

export const HEALTH_SCORE_BANDS = Object.keys(
  HEALTH_SCORE_BAND_ORDER,
) as readonly HealthScoreBand[];

/**
 * The DaisyUI semantics 01 §24.1 names — „rendered with the DaisyUI success/warning/error
 * semantics (doc 04)" — as the §5.2 chip classes.
 */
const BAND_CHIP: Record<HealthScoreBand, string> = {
  GREEN: 'chip-success',
  AMBER: 'chip-warning',
  RED: 'chip-error',
};

/** The same three as a TEXT colour, for the ring's number and the heat list's score cell. */
const BAND_TEXT: Record<HealthScoreBand, string> = {
  GREEN: 'text-success',
  AMBER: 'text-warning',
  RED: 'text-error',
};

/** And as a STROKE, for the ring arc and the sparkline's dots. */
const BAND_STROKE: Record<HealthScoreBand, string> = {
  GREEN: 'stroke-success',
  AMBER: 'stroke-warning',
  RED: 'stroke-error',
};

export function bandChipClass(band: HealthScoreBand): string {
  return BAND_CHIP[band];
}

export function bandTextClass(band: HealthScoreBand): string {
  return BAND_TEXT[band];
}

export function bandStrokeClass(band: HealthScoreBand): string {
  return BAND_STROKE[band];
}

/**
 * The band's Romanian name. `enums.{EnumName}.{VALUE}` is 04 §9.6.3's one sanctioned computed
 * key: the wire value IS the key segment, so a band added server-side renders as its own key
 * path and fails loudly rather than quietly rendering nothing.
 */
export function bandLabel(t: TFunction, band: HealthScoreBand): string {
  return t(`enums.HealthScoreBand.${band}`);
}

/** A registry label: the Romanian name of one `varchar` value. */
export type RegistryLabel = (t: TFunction) => string;

/**
 * The five components of 02 §13.1, in the order `HealthScoreComponents.Weights` lists them —
 * which is the order the bars are drawn in and the order the server sends them in.
 *
 * The WEIGHTS are not here. They arrive on every component of every snapshot
 * (`HealthScoreComponentView.Weight`) precisely because 01 BR-HS-10 lets them change per
 * organization later: „historic snapshots keep the weights they were computed with". A 35
 * spelled in this file would be a second source of truth that goes stale on the day HS-004
 * ships, and it would go stale silently, on a historic snapshot, where nobody would look.
 */
export const HEALTH_COMPONENT_LABELS: Readonly<Record<string, RegistryLabel>> = {
  FINANCIAL: (t) => t('health.components.financial'),
  CONTRACT: (t) => t('health.components.contract'),
  MAINTENANCE: (t) => t('health.components.maintenance'),
  DOCUMENTS: (t) => t('health.components.documents'),
  FISCAL: (t) => t('health.components.fiscal'),
};

/** The five codes, in 02 §13.1's order. `Object.keys` on a literal is insertion order. */
export const HEALTH_COMPONENTS = Object.keys(HEALTH_COMPONENT_LABELS);

/**
 * The components a screen draws: every one the snapshot carries, less fiscal compliance while
 * the fiscal module is withdrawn from view (D-2780). The server then reads no fiscal verdict and
 * scores the component at its full weight, so the total loses no point this list does not name.
 */
export function visibleComponents<T extends { readonly component: string }>(
  components: readonly T[],
): T[] {
  return components.filter((entry) => entry.component !== 'FISCAL' || fiscalVisible());
}

/**
 * One short sentence per component saying what it measures, for the bar's tooltip and the
 * drawer's subtitle. Separate from the label because a heading and an explanation are two
 * different lengths and a translator should be able to see that.
 */
export const HEALTH_COMPONENT_HINTS: Readonly<Record<string, RegistryLabel>> = {
  FINANCIAL: (t) => t('health.componentHints.financial'),
  CONTRACT: (t) => t('health.componentHints.contract'),
  MAINTENANCE: (t) => t('health.componentHints.maintenance'),
  DOCUMENTS: (t) => t('health.componentHints.documents'),
  FISCAL: (t) => t('health.componentHints.fiscal'),
};

/**
 * The seventeen codes of 01 §24.2, grouped by the component that owns them and in the order
 * that section's tables print them.
 *
 * All seventeen are labelled, including the four doc 01 adds to 02 §13.2's ten
 * („completes catalog" — D-1430): at run time there is one catalogue, and
 * `__tests__/enum-parity.test.ts` reads `HealthScorePenalties.Shipped` off the C# and fails
 * when a code has no row here.
 *
 * `FIN_NEG_OPERATING_3M` and `FIN_NEG_ONEOFF_MONTH` are the pair 04 §8.13 singles out — „the
 * structural-vs-one-off distinction is spelled out in the penalty copy" — so the two labels
 * are written to be read against each other: a run of months versus one month, and the second
 * says out loud that a roof job is not a failing property.
 */
export const HEALTH_PENALTY_LABELS: Readonly<Record<string, RegistryLabel>> = {
  // Financial (35) — 01 BR-HS-02
  FIN_NEG_OPERATING_3M: (t) => t('health.penalties.finNegOperating3m'),
  FIN_NEG_ONEOFF_MONTH: (t) => t('health.penalties.finNegOneOffMonth'),
  FIN_ARREARS_AGE: (t) => t('health.penalties.finArrearsAge'),
  FIN_LOW_MARGIN: (t) => t('health.penalties.finLowMargin'),
  // Contract (20) — 01 BR-HS-03
  CON_VACANCY: (t) => t('health.penalties.conVacancy'),
  CON_EXPIRING_60D_NO_ACTION: (t) => t('health.penalties.conExpiringNoAction'),
  CON_MISSING_SIGNED_DOC: (t) => t('health.penalties.conMissingSignedDoc'),
  // Maintenance & revisions (15) — 01 BR-HS-04
  MNT_REVISION_OVERDUE: (t) => t('health.penalties.mntRevisionOverdue'),
  MNT_REVISION_DUE_14D: (t) => t('health.penalties.mntRevisionUpcoming'),
  MNT_TICKET_AGE_30D: (t) => t('health.penalties.mntTicketAge'),
  // Documents & insurance (10) — 01 BR-HS-05
  DOC_EXPIRED: (t) => t('health.penalties.docExpired'),
  DOC_MISSING_PAD: (t) => t('health.penalties.docMissingPad'),
  DOC_EXPIRING_30D: (t) => t('health.penalties.docExpiring'),
  // Fiscal compliance (20) — 01 BR-HS-06
  FIS_REQUIRED_UNPREPARED: (t) => t('health.penalties.fisRequiredUnprepared'),
  FIS_C168_CLOCK_BREACH: (t) => t('health.penalties.fisC168Breach'),
  FIS_DECLARATION_REJECTED: (t) => t('health.penalties.fisDeclarationRejected'),
  FIS_DECLARATION_OVERDUE: (t) => t('health.penalties.fisDeclarationOverdue'),
};

/** The seventeen codes, in 01 §24.2's order. */
export const HEALTH_PENALTY_CODES = Object.keys(HEALTH_PENALTY_LABELS);

/**
 * A registry value's Romanian name, or the raw value when this build has never heard of it.
 *
 * Returning the value itself is the honest fallback, and here it is load-bearing rather than
 * defensive: a snapshot is HISTORY (01 BR-HS-01 — „snapshots are immutable history"), so a
 * breakdown written under a catalogue this client predates, or one whose code was later
 * retired, is a row that must still render. „MNT_REVISION_DUE_14D" beside its points and its
 * Romanian message tells a landlord more than an empty cell, and tells support exactly what to
 * look for.
 */
export function registryLabel(
  table: Readonly<Record<string, RegistryLabel>>,
  value: string,
  t: TFunction,
): string {
  const label = table[value];
  return label === undefined ? value : label(t);
}
